import { CenteredLayout } from '@/components/layouts/CenteredLayout';
import { tenantSelector } from '@/features/configuration/configurationSelector';
import {
  clearManageFlowBooking,
  resetChangeTripsSelection,
} from '@/features/manage/manageActions';
import { resetPurchase } from '@/features/purchase/purchaseActions';
import useDownloadTicket from '@/hooks/useDownloadTicket';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useSearchWidget } from '@/hooks/useSearchWidget';
import { STORAGE_KEYS, useStorage } from '@/hooks/useStorage';
import { TransTenantSpecific } from '@/i18n/trans/tenantSpecific';
import { TransText } from '@/i18n/trans/text';
import { persistor } from '@/store';
import { useDispatch, useSelector } from '@/store/utils';
import type { PurchaseFlowBookingStorageData } from '@/types/storage';
import { breakpoints } from '@/utils/breakpoints';
import {
  GTM_EVENT,
  mapBookingToEcommerceItems,
  trackEcommerceEvent,
} from '@/utils/gtm';
import { getTenantConfiguration } from '@/utils/tenant';
import { Button, Divider, Icons, Typography } from '@fleet/webshop-ui';
import { useEffect, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

const Success = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tenant = useSelector(tenantSelector);
  const configuration = getTenantConfiguration(tenant.value);
  const booking = useSelector((state) => state.purchase.booking);
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const { value: successData, removeValue } =
    useStorage<PurchaseFlowBookingStorageData>(
      sessionStorage,
      STORAGE_KEYS.CHECKOUT_SUCCESS_DATA
    );
  const { setCurrentSearchFormValues } = useSearchWidget();
  const hasFiredPurchaseEvent = useRef(false);

  const {
    isDownloading,
    isPkPassDownloading,
    handleTicketDownload,
    handlePkPassDownload,
  } = useDownloadTicket(successData);

  usePageTitle('purchase');

  useEffect(() => {
    if (!successData?.bookingId) {
      navigate('/purchase', { replace: true });
      return;
    }

    dispatch(resetPurchase());
    // Clear lingering change-booking state too: after a successful exchange
    // payment the operation is fulfilled, so we don't cancel anything — we
    // just drop the local selection/booking so `hasSelection` resets and the
    // managed booking doesn't rehydrate into a stale change-booking view.
    // (`persistor.purge()` only clears persisted storage, not in-memory state.)
    dispatch(resetChangeTripsSelection());
    dispatch(clearManageFlowBooking());
    setCurrentSearchFormValues(undefined);
    persistor.purge();

    return () => {
      removeValue();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!successData?.bookingId) return;

    const confirmedPrice = booking?.confirmedPrice;
    if (
      !hasFiredPurchaseEvent.current &&
      configuration?.enableGoogleTagManager &&
      confirmedPrice?.currency
    ) {
      hasFiredPurchaseEvent.current = true;

      trackEcommerceEvent(GTM_EVENT.PURCHASE, {
        transaction_id: successData.bookingId,
        value: confirmedPrice.amount,
        currency: confirmedPrice.currency,
        items: mapBookingToEcommerceItems(booking),
      });
    }
  }, [booking, configuration?.enableGoogleTagManager, successData]);

  if (!successData?.bookingId) {
    return null;
  }

  return (
    <CenteredLayout>
      <div className="mx-auto mt-10 flex w-full max-w-tablet items-center p-4">
        <div className="grid w-full grid-cols-1 gap-2 laptop:grid-cols-2 laptop:gap-10">
          <div className="flex flex-col">
            <div className="flex items-center gap-3">
              <Icons.checkCircle height={28} width={28} />
              <Typography
                variant={isLaptopOrBigger ? 'title' : 'mobile-title'}
                as="h1"
              >
                <TransText i18nKey="thankYou" />
              </Typography>
            </div>
            <Typography variant="paragraph" className="mt-2">
              <TransText
                i18nKey="bookingNo"
                values={{ number: successData.code }}
              />
            </Typography>
            <Divider className="mb-6 mt-4" />
            {configuration?.showSuccessMessage && (
              <Typography variant="body1">
                <TransTenantSpecific i18nKey="successMessage" />
              </Typography>
            )}
          </div>

          <div className="mt-4 laptop:mt-0">
            <div className="flex flex-col gap-4 py-3 tablet:flex-row tablet:items-center">
              <Button
                className="laptop:w-auto"
                data-testid="download-pdf-button"
                variant="secondary"
                size="medium"
                onClick={handleTicketDownload}
                isDisabled={!successData.bookingId}
                isPending={isDownloading}
                startAdornment={<Icons.download height={16} width={16} />}
              >
                <TransText i18nKey="downloadTickets" />
              </Button>
              <Button
                size="medium"
                className="laptop:w-auto"
                data-testid="download-pkpass-button"
                variant="secondary"
                onClick={handlePkPassDownload}
                isPending={isPkPassDownloading}
                isDisabled={!successData.bookingId}
                startAdornment={<Icons.pkPassWallet height={16} width={16} />}
              >
                <TransText i18nKey="addToWallet" />
              </Button>
            </div>
            <Divider className="mb-6 mt-4" />
            <Typography variant="body1">
              <TransTenantSpecific i18nKey="ticketSent" />
            </Typography>
          </div>
        </div>
      </div>
    </CenteredLayout>
  );
};

export default Success;
